'use client'

import { useState } from 'react'
import { ArrowLeft, Tag, Grid3x3 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import TemplateLibrary from '../../components/admin/TemplateLibrary'
import CanvasDesigner from '../../components/admin/CanvasDesigner'

type ViewMode = 'library' | 'designer'

interface PrintZone {
  id: string
  zone_type: 'text' | 'qr' | 'barcode' | 'image' | 'line' | 'box'
  zone_name: string | null
  x: number
  y: number
  width: number
  height: number
  field_mapping: string | null
  static_content: string | null
}

interface PrintTemplate {
  id: number
  name: string
  description: string | null
  template_type: string
  width: number
  height: number
  scenario: 'item' | 'location' | 'shelf' | 'tote' | 'quantity_update' | null
  category_id: string | null
  category_name: string | null
  canvas_rotation: number
  is_default: boolean
  orientation: 'portrait' | 'landscape'
  active: boolean
  zones: PrintZone[] | null | undefined
  created_at: string
  updated_at: string
}

export default function LabelDesignerPage() {
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<ViewMode>('designer')
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>(undefined)

  const handleSelectTemplate = (template: PrintTemplate) => {
    setSelectedTemplateId(template.id)
    setViewMode('designer')
  }

  const handleCreateNew = () => {
    setSelectedTemplateId(undefined)
    setViewMode('designer')
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      fontFamily: 'Rajdhani, sans-serif',
      padding: '24px'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '48px',
              fontWeight: 700,
              color: '#FF6B35',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: 0,
              textShadow: '0 0 20px rgba(255, 107, 53, 0.6)'
            }}>
              LABEL DESIGNER // TEMPLATE STUDIO
            </h1>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '16px',
              color: '#8A95AA',
              margin: '8px 0 0 0',
              letterSpacing: '1px'
            }}>
              CREATE PROFESSIONAL THERMAL LABELS WITH PRECISION
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)'}
            >
              <ArrowLeft style={{ width: 16, height: 16 }} />
              <span>Back to Admin</span>
            </button>
            <button
              onClick={() => setViewMode('library')}
              style={{
                background: viewMode === 'library'
                  ? 'linear-gradient(135deg, #00F0FF, #00B8D4)'
                  : 'rgba(0, 0, 0, 0.3)',
                border: viewMode === 'library' ? '2px solid #00F0FF' : '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '6px',
                fontWeight: 700,
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                boxShadow: viewMode === 'library' ? '0 0 20px rgba(0, 240, 255, 0.4)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              <Grid3x3 style={{ width: 16, height: 16 }} />
              <span>Library</span>
            </button>
            <button
              onClick={() => setViewMode('designer')}
              style={{
                background: viewMode === 'designer'
                  ? 'linear-gradient(135deg, #00F0FF, #00B8D4)'
                  : 'rgba(0, 0, 0, 0.3)',
                border: viewMode === 'designer' ? '2px solid #00F0FF' : '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '6px',
                fontWeight: 700,
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                boxShadow: viewMode === 'designer' ? '0 0 20px rgba(0, 240, 255, 0.4)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              <Tag style={{ width: 16, height: 16 }} />
              <span>Designer</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div>
        {viewMode === 'library' ? (
          <TemplateLibrary
            onSelectTemplate={handleSelectTemplate}
            onCreateNew={handleCreateNew}
          />
        ) : (
          <CanvasDesigner
            templateId={selectedTemplateId}
            onSave={() => {
              setViewMode('library')
              setSelectedTemplateId(undefined)
            }}
            onCancel={() => {
              setViewMode('library')
              setSelectedTemplateId(undefined)
            }}
          />
        )}
      </div>
    </div>
  )
}
